import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import platformSettingsService, { ProductModeSettings } from '../services/platformSettingsService'

type ProductModeContextValue = {
  humanTutorLayerEnabled: boolean
  isAiOnlyMode: boolean
  loading: boolean
  refresh: () => Promise<void>
}

const ProductModeContext = createContext<ProductModeContextValue | undefined>(undefined)

const DEFAULT_MODE: ProductModeSettings = {
  human_tutor_layer_enabled: false,
}

export const ProductModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<ProductModeSettings>(DEFAULT_MODE)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const next = await platformSettingsService.getProductMode()
      setMode(next)
    } catch (error) {
      console.warn('Product mode unavailable; defaulting to AI-only:', error)
      setMode(DEFAULT_MODE)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<ProductModeContextValue>(
    () => ({
      humanTutorLayerEnabled: mode.human_tutor_layer_enabled,
      isAiOnlyMode: !mode.human_tutor_layer_enabled,
      loading,
      refresh,
    }),
    [mode.human_tutor_layer_enabled, loading, refresh],
  )

  return <ProductModeContext.Provider value={value}>{children}</ProductModeContext.Provider>
}

export const useProductMode = (): ProductModeContextValue => {
  const ctx = useContext(ProductModeContext)
  if (!ctx) {
    throw new Error('useProductMode must be used within ProductModeProvider')
  }
  return ctx
}
